import { Link } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';

const LAST_UPDATED = 'May 31, 2026';

export default function Terms() {
  usePageMeta(
    'Terms of Service | Helpped',
    'Helpped terms of service — rules and guidelines for using the Helpped app.',
  );
  return (
    <div className="min-h-screen flex flex-col">
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 group">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center text-white font-bold text-sm"
              style={{ background: 'hsl(var(--primary))' }}
            >
              H
            </div>
            <span className="font-bold text-gray-900 tracking-tight group-hover:opacity-80 transition-opacity">
              Helpped
            </span>
          </Link>
          <span className="text-gray-300">/</span>
          <span className="text-sm text-gray-500">Terms of Service</span>
        </div>
      </nav>

      <main className="flex-1 px-6 py-12">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl font-extrabold text-gray-900 mb-1">Terms of Service</h1>
          <p className="text-sm text-gray-400 mb-10">Last updated: {LAST_UPDATED}</p>

          <Section title="1. Acceptance of terms">
            <p>By downloading, installing, or using Helpped ("the App"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use the App.</p>
          </Section>

          <Section title="2. Who can use Helpped">
            <p>You must be at least 13 years old to use Helpped. By using the App, you confirm that you meet this requirement.</p>
          </Section>

          <Section title="3. Your account">
            <p>You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. Notify us immediately at <a href="mailto:hello@helpped.com" className="text-green-700 hover:underline">hello@helpped.com</a> if you suspect unauthorized access.</p>
          </Section>

          <Section title="4. User content">
            <p>You retain ownership of content you post on Helpped (posts, photos, comments). By posting, you grant Helpped a non-exclusive, royalty-free, worldwide license to display, distribute, and reproduce your content within the App solely to operate the service.</p>
            <p>You are solely responsible for your content. You agree not to post content that:</p>
            <ul>
              <li>Is false, misleading, or defamatory.</li>
              <li>Harasses, threatens, or harms any person.</li>
              <li>Infringes the intellectual property rights of others.</li>
              <li>Violates any applicable law or regulation.</li>
              <li>Contains spam, advertising, or unsolicited promotions.</li>
            </ul>
            <p>We reserve the right to remove any content that violates these Terms or that we determine, at our sole discretion, is harmful to the community.</p>
          </Section>

          <Section title="5. Prohibited conduct">
            <p>You agree not to:</p>
            <ul>
              <li>Use the App for any unlawful purpose.</li>
              <li>Impersonate any person or entity.</li>
              <li>Reverse engineer, decompile, or attempt to extract the App's source code.</li>
              <li>Use automated tools to scrape or collect data from the App without our express written permission.</li>
              <li>Interfere with or disrupt the App's infrastructure.</li>
            </ul>
          </Section>

          <Section title="6. Location data">
            <p>The App uses your location to show neighborhood-relevant content. You can control location permissions in your device settings. Restricting location access will limit some features.</p>
          </Section>

          <Section title="7. Intellectual property">
            <p>All content, design, code, and branding in the App (excluding user content) is owned by Helpped and protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, or distribute any part of the App without our written permission.</p>
          </Section>

          <Section title="8. Disclaimer of warranties">
            <p>The App is provided "as is" without warranties of any kind, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not guarantee that the App will be error-free, uninterrupted, or secure.</p>
          </Section>

          <Section title="9. Limitation of liability">
            <p>To the maximum extent permitted by law, Helpped shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of or inability to use the App, even if we have been advised of the possibility of such damages. Our total liability to you shall not exceed $100.</p>
          </Section>

          <Section title="10. Termination">
            <p>We may suspend or terminate your access to Helpped at any time, with or without notice, for conduct that we determine violates these Terms or is harmful to other users, us, or third parties. You may delete your account at any time through the App settings.</p>
          </Section>

          <Section title="11. Governing law">
            <p>These Terms are governed by the laws of the State of Washington, without regard to its conflict of law provisions. Any disputes arising from these Terms or your use of the App shall be resolved in the state or federal courts located in King County, Washington.</p>
          </Section>

          <Section title="12. Changes to these terms">
            <p>We may update these Terms from time to time. We'll notify you of material changes via email or in-app notification. Continued use of the App after the effective date of revised Terms constitutes acceptance.</p>
          </Section>

          <Section title="13. Contact">
            <p>Questions about these Terms? Email us at <a href="mailto:hello@helpped.com" className="text-green-700 hover:underline">hello@helpped.com</a>.</p>
          </Section>
        </div>
      </main>

      <footer className="px-6 py-8 border-t border-gray-100">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-400">
          <span>© {new Date().getFullYear()} Helpped. Auburn, WA.</span>
          <div className="flex gap-6">
            <Link to="/" className="hover:text-gray-600 transition-colors">Home</Link>
            <Link to="/privacy" className="hover:text-gray-600 transition-colors">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-lg font-bold text-gray-900 mb-3">{title}</h2>
      <div className="text-gray-600 space-y-3 leading-relaxed text-sm">{children}</div>
    </section>
  );
}
